import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import logo from '../assets/logo-new.svg';
import './Navbar.css';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState('home');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  // Use useCallback to create stable event handler references
  const closeMenuOnClickOutside = useCallback((e) => {
    if (!e.target.closest('.navbar-container')) {
      setIsMenuOpen(false);
    }
  }, []);

  // Toggle menu with proper cleanup
  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prevState => {
      const newMenuState = !prevState;
      
      // Always show navbar when menu is open
      if (newMenuState) {
        setIsVisible(true);
      }
      
      return newMenuState;
    });
  }, []);

  // Close menu when a navigation item is clicked
  const handleCloseMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  // Add/remove document click listener based on menu state
  useEffect(() => {
    if (isMenuOpen) {
      // Small delay to avoid immediate triggering
      const timer = setTimeout(() => {
        document.addEventListener('click', closeMenuOnClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', closeMenuOnClickOutside);
      };
    }
    
    return () => {
      document.removeEventListener('click', closeMenuOnClickOutside);
    };
  }, [isMenuOpen, closeMenuOnClickOutside]);

  // Scroll handling effect with proper cleanup
  useEffect(() => {
    // Only run this effect in browser environment
    if (typeof window === 'undefined') return;
    
    // Store previous scroll position outside the effect
    let prevScrollPos = window.scrollY;
    let ticking = false;
    
    const handleScroll = () => {
      // Throttle scroll events for better performance
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollPos = window.scrollY;
          
          // Check if window is defined and document exists
          if (typeof window === 'undefined' || !document || !document.documentElement) {
            ticking = false;
            return;
          }
          
          // Update scroll progress
          const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
          const progress = Math.min(100, Math.max(0, (currentScrollPos / totalScroll) * 100));
          setScrollProgress(progress);
          
          // Check if page is scrolled to add background
          setIsScrolled(currentScrollPos > 50);
          
          // CRITICAL: Navbar visibility logic
          // 1. Always show navbar when menu is open
          if (isMenuOpen) {
            setIsVisible(true);
          }
          // 2. Always show navbar at the top of the page
          else if (currentScrollPos <= 100) {
            setIsVisible(true);
          }
          // 3. Show navbar when scrolling up, hide when scrolling down
          else {
            const isScrollingDown = currentScrollPos > prevScrollPos;
            
            // Only change visibility if we've scrolled at least 10px to avoid flickering
            if (Math.abs(currentScrollPos - prevScrollPos) > 10) {
              // When scrolling up, show navbar
              if (!isScrollingDown) {
                setIsVisible(true);
              } 
              // When scrolling down, hide navbar
              else {
                setIsVisible(false);
              }
            }
          }
          
          // Update active section
          const sections = ['home', 'about', 'services', 'blog', 'resources', 'contact'];
          for (const section of sections) {
            const element = document.getElementById(section);
            if (element) {
              const rect = element.getBoundingClientRect();
              if (rect.top <= 100 && rect.bottom >= 100) {
                setActiveSection(section);
                break;
              }
            }
          }
          
          // Save current position for next comparison
          prevScrollPos = currentScrollPos;
          ticking = false;
        });
        
        ticking = true;
      }
    };

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial call to set up the navbar
    handleScroll();
    
    // Clean up
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isMenuOpen]); // Only re-run when menu state changes

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''} ${isVisible ? 'visible' : 'hidden'}`}>
      <div className="navbar-container">
        <Link to="/" className="navbar-brand">
          <img src={logo} alt="Clarity Impact Finance Logo" className="navbar-logo" />
        </Link>

        <button className={`menu-toggle ${isMenuOpen ? 'active' : ''}`} onClick={toggleMenu}>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>

        <div className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
          <ul>
            <li className={activeSection === 'home' ? 'active' : ''}>
              <Link to="/" onClick={handleCloseMenu}>Home</Link>
            </li>
            <li className={activeSection === 'about' ? 'active' : ''}>
              <Link to="/#about" onClick={handleCloseMenu}>About</Link>
            </li>
            <li className={activeSection === 'services' ? 'active' : ''}>
              <Link to="/#services" onClick={handleCloseMenu}>Services</Link>
            </li>
            <li className={activeSection === 'blog' ? 'active' : ''}>
              <Link to="/#blog" onClick={handleCloseMenu}>Blog</Link>
            </li>
            <li className={activeSection === 'resources' ? 'active' : ''}>
              <Link to="/#resources" onClick={handleCloseMenu}>Resources</Link>
            </li>
            <li className={activeSection === 'contact' ? 'active' : ''}>
              <Link to="/#contact" onClick={handleCloseMenu}>Contact</Link>
            </li>
          </ul>
        </div>
      </div>
      
      {/* Progress indicator bar */}
      <div className="scroll-progress-container">
        <div 
          className="scroll-progress-bar" 
          style={{ width: `${scrollProgress}%` }}
        ></div>
      </div>
    </nav>
  );
};

export default Navbar;